import { useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Camera, Copy, GitBranch, ShieldCheck, Sparkles, Check, Search, Hash, Trash2 } from "lucide-react";
import { RedTwinView } from "@/components/RedTwinView";
import { AuthImg } from "@/components/AuthImg";
import { PhotoLightbox } from "@/components/PhotoLightbox";
import { ChoiceButton } from "@/components/ChoiceButton";
import { copyText } from "@/lib/copyText";
import { numberWords, ROLE_CHIP } from "@/lib/numberWords";

// THE PART'S NUMBERS, UNDER THE TAG THEY CAME OFF (his layout, approved 2026-10-13; the same way as Ask AI, 23 Oct).
//
// THE RULES THIS SCREEN KEEPS, and they are his words:
//  1. THE ASSEMBLY ITSELF IS AT THE TOP — the part he names and sells. Its own numbers are the only
//     ones that can be its OEM / Serial #. Each bolt-on follows under its name (and brand), with its tag's photo.
//  2. A BOLT-ON COMPONENT'S VALUES SAY WHAT THEY ARE — one part number for that component, and the
//     serial, date code, rating or customer number beside it, each labelled. Part numbers first; a labelled value
//     (date code, rating, quantity) sits under them in a quieter style.
//  3. EVERY AI-READ VALUE HAS ITS OWN ✓verify, and there is NO "verify all" — not on a tag, not on
//     a group, not on this screen. (The Identify page shows this same list with no ✓verify: his 8 Oct rule.)
//  4. A RATING, A DATE CODE OR A QUANTITY IS TEXT, NOT A PART NUMBER. It is findable, it is never
//     scored as a number, until somebody taps "search this as a number".
//  5. EVERY NUMBER SHOWS THE PHOTO IT WAS READ FROM, and the photo opens (his instruction,
//     2026-10-14). The words on the buttons come from ONE shared place (`lib/numberWords`).
//  6. EVERY NUMBER HAS COPY, and every choice is a button that looks like one (ChoiceButton).
const ASSEMBLY = "__assembly__";

export function groupNumbers(nums) {
  const groups = [];
  nums.forEach((n) => {
    const key = n.group_id || ASSEMBLY;
    let g = groups.find((x) => x.key === key);
    if (!g) {
      g = { key, label: n.group_label || "The assembly itself", rows: [], photo: null, brands: {} };
      groups.push(g);
    }
    g.rows.push(n);
    g.photo = g.photo || n.group_photo_id || null;
    if (n.brand) g.brands[n.brand] = (g.brands[n.brand] || 0) + 1;
  });
  groups.forEach((g) => {
    g.brand = Object.entries(g.brands).sort((a, b) => b[1] - a[1])[0]?.[0] || "";
    g.rows = [...g.rows.filter((n) => n.indexed !== false), ...g.rows.filter((n) => n.indexed === false)];
  });
  return groups.sort((a, b) => (a.key === ASSEMBLY ? -1 : b.key === ASSEMBLY ? 1 : 0));
}

function NumberRow({ n, canManage, allowVerify, isIdentity, busy, promoting, setPromoting, act, openShot, setTwinFor, onSpinOff, onDelete }) {
  const machineRead = n.read_by === "ai" && !n.verified_at;
  const textOnly = n.indexed === false;
  const role = n.number_role && n.number_role !== "service_part" ? ROLE_CHIP[n.number_role] || n.number_role.replace("_", " ") : "";
  const shotId = n.photo_file_id || n.group_photo_id || null;
  const W = numberWords(!!n.group_id);
  const tone = textOnly ? "border-slate-200 bg-slate-50/70 text-slate-500" : machineRead ? "border-violet-200 bg-violet-50/40" : "border-slate-200 bg-white";
  return (
    <div data-testid={`part-detail-number-${n.id}`} className={`flex flex-wrap items-center gap-2 rounded-lg border px-2.5 py-1.5 text-sm ${tone}`}>
      {shotId && (
        <button type="button" data-testid={`part-number-photo-${n.id}`} title={W.photoHint} onClick={() => openShot(shotId)}
          className="shrink-0 w-8 h-8 rounded-md overflow-hidden border border-slate-200 bg-slate-50 hover:border-[#E0781A] focus:outline-none focus:ring-2 focus:ring-[#E0781A]">
          <AuthImg fileId={shotId} alt="the photo this number was read from" className="w-full h-full object-cover" />
        </button>
      )}
      <span data-testid={`part-number-value-${n.id}`} className={`font-mono font-bold ${textOnly ? "text-slate-600" : "text-slate-800"}`}>{n.value}</span>
      {n.group_primary && <Badge data-testid={`part-number-group-primary-${n.id}`} className="rounded-full border border-sky-200 bg-sky-50 text-sky-700 text-[10px]"><Hash className="w-3 h-3 mr-0.5" />this tag's part #</Badge>}
      {role && <Badge data-testid={`part-number-role-${n.id}`} className="rounded-full border border-slate-200 bg-slate-50 text-slate-600 text-[10px]">{role}</Badge>}
      {n.brand && <span className="text-slate-500 text-xs">{n.brand}</span>}
      {n.owner && n.owner !== "unknown" && <Badge className="rounded-full border-0 bg-slate-100 text-slate-600 text-[10px]">{(n.owner_name || n.owner).replace(/_/g, " ")}</Badge>}
      {n.source && <Badge data-testid={`part-number-source-${n.id}`} className="rounded-full border-0 bg-slate-500 text-white text-[10px] uppercase">{n.source.replace(/_/g, " ")}</Badge>}
      {n.ocr_confidence && <Badge className="rounded-full border border-slate-300 bg-white text-slate-500 text-[10px]">OCR {n.ocr_confidence}</Badge>}
      {machineRead && <Badge data-testid={`part-number-ai-read-${n.id}`} className="rounded-full border border-violet-300 bg-white text-violet-700 text-[10px]"><Sparkles className="w-3 h-3 mr-0.5" />AI read · not verified</Badge>}
      {n.verified_at && <Badge data-testid={`part-number-verified-${n.id}`} className="rounded-full border-0 bg-emerald-600 text-white text-[10px]"><ShieldCheck className="w-3 h-3 mr-0.5" />checked by a person</Badge>}
      {n.needs_retake && !n.verified_at && <Badge data-testid={`part-detail-number-retake-${n.id}`} title={n.retake_reason || ""} className="rounded-full border border-red-200 bg-red-50 text-[#B01E28] text-[10px]"><Camera className="w-3 h-3 mr-0.5" />check the photo</Badge>}
      {textOnly && <Badge data-testid={`part-number-text-only-${n.id}`} title="Found by searching the text, but never treated as a part number." className="rounded-full border border-slate-200 bg-white text-slate-500 text-[10px]">text, not a part #</Badge>}
      <div className="flex flex-wrap items-center gap-1.5 ml-auto">
        {n.needs_retake && !n.verified_at && (
          <ChoiceButton tone="danger" data-testid={`part-number-see-twin-${n.id}`} onClick={() => setTwinFor(n.value)} className="h-8 min-h-8">See them side by side</ChoiceButton>
        )}
        {/* ✓VERIFY — one value, one click. Shown only where no field chip covers it, so
            there is never a second verify for the same number. */}
        {allowVerify && canManage && machineRead && !isIdentity(n) && (
          <ChoiceButton tone="sky" data-testid={`part-number-verify-${n.id}`} disabled={busy === `${n.id}:verify-read`} className="h-8 min-h-8"
            onClick={() => act(n, "verify-read", `${n.value} checked — recorded against your name`)} title="Confirm you checked this value against the tag">
            <Check className="w-3 h-3 mr-1" />Verify
          </ChoiceButton>
        )}
        {canManage && textOnly && (
          <ChoiceButton data-testid={`part-number-search-as-number-${n.id}`} disabled={busy === `${n.id}:search-as-number`} className="h-8 min-h-8"
            onClick={() => act(n, "search-as-number", `${n.value} is now searched as a part number`)}>
            <Search className="w-3 h-3 mr-1" />Search this as a number
          </ChoiceButton>
        )}
        {/* THE DELIBERATE PATH, AND IT ASKS TWICE — a component's number never becomes the assembly's by accident. */}
        {canManage && n.group_id && (
          <ChoiceButton tone={promoting === n.id ? "amber" : "plain"} data-testid={`part-number-adopt-${n.id}`} disabled={busy === `${n.id}:adopt-as-assembly`} className="h-8 min-h-8"
            onClick={() => { if (promoting !== n.id) { setPromoting(n.id); return; } act(n, "adopt-as-assembly", `${n.value} is now one of the assembly's own numbers`); }}>
            {promoting === n.id ? W.promoteConfirm : W.promote}
          </ChoiceButton>
        )}
        <ChoiceButton data-testid={`part-number-copy-${n.id}`} onClick={() => copyText(n.value)} className="h-8 min-h-8"><Copy className="w-3 h-3 mr-1" />Copy</ChoiceButton>
        {canManage && onSpinOff && (
          <ChoiceButton data-testid={`spinoff-num-open-${n.id}`} title="Spin this number's component off into its own part" aria-label="Spin off"
            onClick={() => onSpinOff(n.id)} className="h-8 min-h-8 px-2"><GitBranch className="w-3.5 h-3.5 mr-1" />Spin off</ChoiceButton>
        )}
        {onDelete && (
          <ChoiceButton tone="danger" data-testid={`number-delete-${n.id}`} title="Remove this number" aria-label="Remove this number"
            onClick={() => onDelete(n.id)} className="h-8 min-h-8 px-2"><Trash2 className="w-3.5 h-3.5" /></ChoiceButton>
        )}
      </div>
      {n.needs_retake && !n.verified_at && n.retake_reason && (
        <div data-testid={`part-detail-number-retake-reason-${n.id}`} className="w-full text-[11px] font-semibold text-[#B01E28]">{n.retake_reason}</div>
      )}
      {(n.unsure_chars || []).length > 0 && (
        <div data-testid={`part-detail-number-unclear-${n.id}`} className="w-full text-[11px] text-amber-700">
          Unclear chars: {n.unsure_chars.join(", ")} — verify against the physical stamp.
        </div>
      )}
    </div>
  );
}

function GroupHeader({ g, gi, openShot }) {
  const asm = g.key === ASSEMBLY;
  return (
    <div className="flex items-center gap-2 px-2.5 pt-2">
      {!asm && g.photo && (
        <button type="button" data-testid={`part-numbers-group-photo-${gi}`} title="Open the photo of this tag" onClick={() => openShot(g.photo)}
          className="shrink-0 w-9 h-9 rounded-md overflow-hidden border border-slate-200 bg-slate-50 hover:border-[#E0781A]">
          <AuthImg fileId={g.photo} thumb alt={`the tag of ${g.label}`} className="w-full h-full object-cover" />
        </button>
      )}
      <div className="min-w-0">
        <span data-testid={`part-numbers-group-label-${gi}`} className="text-[10px] font-bold uppercase tracking-widest text-slate-700">{g.label}</span>
        <div className="text-[10px] text-slate-500">
          {asm ? <span className="text-amber-800">the part you're selling</span> : "a bolt-on · read off its own tag"}
          {g.brand && <span data-testid={`part-numbers-group-brand-${gi}`}> · brand <b>{g.brand}</b></span>}
          {" · "}{g.rows.length} number{g.rows.length === 1 ? "" : "s"}
        </div>
      </div>
    </div>
  );
}

export function PartNumbersGrouped({ part, canManage, onChanged, onSpinOff, allowVerify = true, onDelete }) {
  const [busy, setBusy] = useState("");
  const [promoting, setPromoting] = useState("");
  const [twinFor, setTwinFor] = useState(null);   // red twin view: the value being compared
  const [shotIdx, setShotIdx] = useState(null);   // the source photo opened full-size
  const shots = (part.photos || []).filter((p) => p && p.file_id && !p.is_archived);
  const openShot = (fileId) => {
    const i = shots.findIndex((p) => p.file_id === fileId);
    if (i < 0) { toast.message("That photo is no longer on this part."); return; }
    setShotIdx(i);
  };
  const identity = new Set([part.oem_normalized, part.tag_serial_normalized].filter(Boolean));
  const isIdentity = (n) => identity.has(n.normalized) || n.value === part.oem_number || n.value === part.tag_serial;
  const groups = groupNumbers(part.numbers || []);

  const act = async (n, path, okMsg) => {
    setBusy(`${n.id}:${path}`);
    try {
      await api.post(`/parts/${part.id}/numbers/${n.id}/${path}`);
      toast.success(okMsg);
      onChanged?.();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Couldn't do that.");
    }
    setBusy("");
    setPromoting("");
  };
  const rowProps = { canManage, allowVerify, isIdentity, busy, promoting, setPromoting, act, openShot, setTwinFor, onSpinOff, onDelete };

  return (
    <div className="space-y-2.5" data-testid="part-numbers-grouped">
      {groups.map((g, gi) => (
        <div key={g.key} data-testid={`part-numbers-group-${gi}`}
          className={`rounded-xl border ${g.key === ASSEMBLY ? "border-amber-200 bg-amber-50/30" : "border-slate-200"}`}>
          <GroupHeader g={g} gi={gi} openShot={openShot} />
          <div className="p-2.5 space-y-1.5">
            {g.rows.map((n) => <NumberRow key={n.id} n={n} {...rowProps} />)}
          </div>
        </div>
      ))}
      {/* ONE TAP ON A RED NUMBER (his instruction, 2026-10-13) — the look-alike and the part holding
          it, side by side. This view only shows; it never edits behind the crew's back. */}
      <RedTwinView partId={part.id} value={twinFor} open={!!twinFor}
        onOpenChange={(v) => !v && setTwinFor(null)} />
      {/* THE PHOTO A NUMBER CAME OFF, full size, from the list at the bottom of the part. */}
      <PhotoLightbox photos={shots} index={shotIdx} testidPrefix="part-number-shot"
        onIndex={setShotIdx} onClose={() => setShotIdx(null)}
        fixScope="part" fixRecordId={part.id} onFixed={onChanged} />
    </div>
  );
}
